import React, { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import {
  Typography, Spin, List, Avatar, Divider, Checkbox,
  Button, message
} from "antd";

const { Title } = Typography;
const LS_KEY   = "simolapps_state";
const API      = {
  artistInfo : id => `https://simolmedia.tw1.ru/api/get_tracks_by_artist_id.php?artist_id=${id}`,
  queue      : id => `https://simolmedia.tw1.ru/api/get_download_queue.php?user_id=${id}`,
  purchased  : "https://simolmedia.tw1.ru/api/get_purchased_tracks.php",
  addToQueue : "https://simolmedia.tw1.ru/api/add_to_download_queue.php"
};

export default function ArtistPage() {
  const { artistId } = useParams();
  const { state }    = useLocation();

  /* ---------- state ---------- */
  const [info     , setInfo    ] = useState(null);
  const [loading  , setLoading ] = useState(true);
  const [selected , setSelected] = useState([]);

  const [queueIds , setQ] = useState([]);   // уже в корзине
  const [purchased, setP] = useState([]);   // купленные

  /* ---------- user ---------- */
  const userId = (()=> {
    try { return JSON.parse(localStorage.getItem(LS_KEY))?.user?.id ?? null; }
    catch { return null; }
  })();

  /* ---------- load ---------- */
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);

        /* 1. полная инфа артиста + треков */
        const data = await fetch(API.artistInfo(artistId)).then(r => r.json());
        setInfo(data);

        /* 2. корзина + купленные для чек-боксов */
        if (userId) {
          const [q , p] = await Promise.all([
            fetch(API.queue(userId)).then(r => r.json()),
            fetch(API.purchased,{
              method:"POST",
              headers:{ "Content-Type":"application/json" },
              body:JSON.stringify({ user_id:userId })
            }).then(r=>r.json())
          ]);

          setQ((q.paid ?? q.track_ids ?? []).map(t => Number(t.id || t)));
          setP((p.track_ids ?? []).map(Number));

          /* ——— debug ——— */
          console.log("[ART] треки артиста:", data.tracks);
          console.log("[ART] купленные id:",   p.track_ids);
          console.log("[ART] в корзине id:",   q.paid ?? q.track_ids);
        }
      } catch(e) {
        console.error(e);
        message.error("Не удалось загрузить данные");
      } finally {
        setLoading(false);
      }
    })();
  }, [artistId, userId]);

  /* ---------- helpers ---------- */
  const toggleTrack = (id, checked, disabled) => {
    if (disabled) { message.info("Этот трек уже куплен"); return; }
    setSelected(prev => checked
      ? [...prev, id]
      : prev.filter(x => x !== id)
    );
  };

  const addToQueue = async () => {
    if (!userId)          return message.error("Авторизуйтесь");
    if (selected.length===0) return message.warning("Нет выбранных треков");

    try {
      const r = await fetch(API.addToQueue,{
        method:"POST",
        headers:{ "Content-Type":"application/json" },
        body:JSON.stringify({ user_id:userId, track_ids:selected })
      }).then(x=>x.json());

      if (r.success){
        message.success("Добавлено в корзину");
        setQ(q => [...q, ...selected]);
        setSelected([]);
      } else {
        message.error(r.message || "Ошибка добавления");
      }
    } catch { message.error("Ошибка сети"); }
  };

  /* ---------- render ---------- */
  if (loading)            return <Spin tip="Загрузка…"/>;
  if (!info || info.error) return <p>Артист не найден 😔</p>;

  const { artist, albums, tracks } = info;

  const artistCover = artist.thumbnail?.startsWith("http")
    ? artist.thumbnail
    : `https://audiobuzz-web.tmweb.ru/uploads/thumbnails/artists/${artist.thumbnail}`;

  return (
    <div style={{ padding: 20 }}>
      <Title level={2}>{state?.artistName || artist.name}</Title>
      <img
        src={artistCover}
        alt={artist.name}
        style={{ width: 220, height: 220, objectFit: "cover", borderRadius: 6 }}
      />

      {!!albums.length && (
        <>
          <Divider orientation="left">Альбомы&nbsp;({albums.length})</Divider>
          <ul>
            {albums.map(a => <li key={a.album_id}>{a.album_name}</li>)}
          </ul>
        </>
      )}

      <Divider orientation="left">Треки&nbsp;({tracks.length})</Divider>

      <List
        itemLayout="horizontal"
        dataSource={tracks}
        renderItem={t => {
          const disabled = purchased.includes(t.id);
          const checked  = disabled || queueIds.includes(t.id) || selected.includes(t.id);

          const cover = t.track_cover_photo?.startsWith("http") || !t.track_cover_photo
            ? t.track_cover_photo
            : `https://audiobuzz-web.tmweb.ru/uploads/thumbnails/media/${t.track_cover_photo}`;

          return (
            <List.Item>
              <Checkbox
                disabled={disabled}
                checked={checked}
                onChange={e => toggleTrack(t.id, e.target.checked, disabled)}
                style={{ marginRight: 16, opacity: disabled ? 0.4 : 1 }}
              />
              <List.Item.Meta
                avatar={<Avatar shape="square" size={64} src={cover}/>}
                title={t.title}
                description={`Длительность: ${Math.floor(t.duration/1000)} сек`}
              />
            </List.Item>
          );
        }}
      />

      {!!tracks.length && (
        <div style={{ marginTop: 24, textAlign: "center" }}>
          <Button type="primary" onClick={addToQueue} disabled={!selected.length}>
            Добавить в корзину ({selected.length})
          </Button>
        </div>
      )}
    </div>
  );
}
